import { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { capitalizeFirstLetter } from '../../utils';
import { apiClient } from '../../api_actions';
import { orbitalSpinner } from '../LoadingOrbitalSpinner/LoadingOrbitalSpinner';
import { chartTheme } from '../../constants/chartTheme';

// Separate data transformation
const transformDetectionEventsData = (rawData) => {
    // Get all unique detection type names (excluding 'date' key)
    const detectionTypeNames = [];
    rawData.forEach(dateRow => {
        Object.keys(dateRow).forEach(key => {
            if (key !== 'date' && !detectionTypeNames.includes(key)) {
                detectionTypeNames.push(key);
            }
        });
    });

    // Convert Unix timestamps (seconds) to MM/DD/YYYY strings and sort by date
    const finalData = rawData
        .slice()
        .sort((a, b) => a.date - b.date)
        .map(dateRow => ({
            ...dateRow,
            date: dayjs.unix(dateRow.date).format('MM/DD/YYYY')
        }));

    // Calculate total events per detection type for sorting
    const detectionTypeTotals = {};
    detectionTypeNames.forEach(detectionType => {
        detectionTypeTotals[detectionType] = rawData.reduce((sum, row) => sum + (Number(row[detectionType]) || 0), 0);
    });

    // Sort detection types by total events descending so legend reflects highest-volume types first
    const sortedDetectionTypeNames = [...detectionTypeNames].sort((a, b) => detectionTypeTotals[b] - detectionTypeTotals[a]);

    // Create series for each detection type in sorted order
    const series = sortedDetectionTypeNames.map(detectionType => ({
        type: 'line',
        xKey: 'date',
        xName: 'Date',
        yKey: detectionType,
        yName: capitalizeFirstLetter(detectionType.replace(/_/g, ' ')),
        interpolation: {
            type: 'smooth',
        },
    }));

    return { data: finalData, series };
};

// Separate API call
const fetchDetectionEventsData = async (startAndEndUnixDatesParams = '') => {
    try {
        const res = await apiClient.post(`read/dashboardlinecharts?mode=detection_type&${startAndEndUnixDatesParams()}`);
        if (res && Array.isArray(res)) {
            return transformDetectionEventsData(res);
        } else if (res && Array.isArray(res.data)) {
            return transformDetectionEventsData(res.data);
        }
        return { data: [], series: [] };
    } catch (error) {
        console.error('Failed to load data:', error);
        return { data: [], series: [] };
    }
};

// Factory function - simplified
const createDetectionEventsConfig = (data = [], series = [], formatDateRange = () => '') => ({
    theme: chartTheme,
    axes: [
        {
            type: 'category',
            position: 'bottom',
        },
        {
            type: 'number',
            position: 'left',
        },
    ],
    legend: {
        enabled: true,
    },
    series: series,
    title: { text: 'Detection Events' },
    subtitle: { text: formatDateRange() },
    data: data,
    overlays: {
        loading: {
            renderer: orbitalSpinner,
        },
    },
});

export const DetectionEventsChartLogic = (startAndEndUnixDatesParams = '', formatDateRange = () => '') => {
    const [detectionEventsConfig, setDetectionEventsConfig] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            const result = await fetchDetectionEventsData(startAndEndUnixDatesParams);
            setDetectionEventsConfig(createDetectionEventsConfig(result.data, result.series, formatDateRange));
            setLoading(false);
        };

        loadData();
    }, [startAndEndUnixDatesParams, formatDateRange]);

    return {
        chartConfig: detectionEventsConfig,
        loading,
    };
};
